document.addEventListener('DOMContentLoaded', () => {
  /**
   * Creates a message element with consistent styling
   * @param {string} type - 'success' or 'error'
   * @param {string} message - The message to display
   * @returns {HTMLElement} The created message element
   */
  const createMessageElement = (type, message) => {
    const messageElement = document.createElement('div');
    messageElement.id = type === 'success' ? 'submit-success-message' : 'submit-error-message';
    messageElement.innerHTML = `
      <div>
        <div class="text-with-icon">
          ${type === 'success' ? `
            <svg role="presentation" focusable="false" stroke-width="2" width="18" height="18" class="icon icon-success" style="--icon-height: 18px" viewBox="0 0 18 18">
              <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
              <path d="M5 8.8L7.62937 11.6L13 6" stroke="#ffffff" fill="none"></path>
            </svg>
          ` : `
            <svg role="presentation" focusable="false" width="18" height="18" class="text-error icon icon-error" style="--icon-height: 18px" viewBox="0 0 18 18">
              <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
              <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
            </svg>
          `}
          <p class="text-sm">${message}</p>
        </div>
      </div>
    `;
    
    return messageElement;
  };



  /**
   * Resets the click and collect form state to its initial condition
   * - Hides the form container
   * - Shows the locations section
   * - Clears all form fields and selected location display
   * - Removes any existing success/error messages
   * - Resets submit button text and state
   */
  const resetFormState = () => {
    // Get fresh references to form elements (in case they were re-rendered)
    const currentFormContainer = document.getElementById('click-and-collect-form__container');
    const currentSelectedLocationName = document.getElementById('selected-location-name');
    const currentLocationIdInput = document.querySelector('input[name="location_id"]');
    const currentSubmitButton = document.getElementById('click-and-collect-form__submit');
    
    // Hide the form
    if (currentFormContainer) currentFormContainer.style.display = 'none';
    
    // Show the locations section
    const locationsSection = document.querySelector('[id^="locations-section-"]');
    if (locationsSection) locationsSection.style.display = 'block';
    
    // Clear the selected location display
    if (currentSelectedLocationName) currentSelectedLocationName.textContent = '';
    
    // Clear the location ID input
    if (currentLocationIdInput) currentLocationIdInput.value = '';
    
    // Reset submit button text and state
    if (currentSubmitButton) {
      currentSubmitButton.textContent = window.themeVariables.strings.clickAndCollectSubmit;
      currentSubmitButton.disabled = false;
    }
    
    // Remove any messages
    clearMessage();
  };

  /**
   * Removes any existing success or error message from the DOM
   * Clears messages from the submit message section
   */
  const clearMessage = () => {
    const submitMessageSection = document.getElementById('submit-message-section');
    if (submitMessageSection) {
      // Remove any existing success or error messages
      const existingSuccessMessage = submitMessageSection.querySelector('#submit-success-message');
      const existingErrorMessage = submitMessageSection.querySelector('#submit-error-message');
      
      if (existingSuccessMessage) existingSuccessMessage.remove();
      if (existingErrorMessage) existingErrorMessage.remove();
    }
  };

  /**
   * Sets up event listeners for form elements
   */
  const setupFormEventListeners = () => {
    const submitButton = document.getElementById('click-and-collect-form__submit');
    const cancelButton = document.getElementById('click-and-collect-form__cancel');
    
    if (submitButton) {
      submitButton.removeEventListener('click', handleFormSubmit);
      submitButton.addEventListener('click', handleFormSubmit);
    }
    
    if (cancelButton) {
      cancelButton.removeEventListener('click', handleCancelClick);
      cancelButton.addEventListener('click', handleCancelClick);
    }
  };

  /**
   * Handles the cancel button click event
   * Calls resetFormState to clear the form and return to location selection
   */
  const handleCancelClick = () => {
    resetFormState();
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = document.getElementById('click-and-collect-form');
    const submitButton = document.getElementById('click-and-collect-form__submit');
    
    if (submitButton) {
      submitButton.textContent = window.themeVariables.strings.clickAndCollectSending;
      submitButton.disabled = true;
    }

    try {
      const formData = Object.fromEntries(new FormData(form).entries());
  
      const response = await fetch('https://hook.eu2.make.com/jhjpgv68k1ahbp85eprvydux7bg4ctsp', {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Webhook failed: ${response.status} ${errorText}`);
      }

      resetFormState();
      const message = createMessageElement('success', window.themeVariables.strings.clickAndCollectSuccess);
      const submitMessageSection = document.getElementById('submit-message-section');
      if (submitMessageSection) submitMessageSection.appendChild(message);
    } catch (error) {
      console.error(error.message);      
      resetFormState();
      const errorMessage = createMessageElement('error', window.themeVariables.strings.clickAndCollectError);
      const submitMessageSection = document.getElementById('submit-message-section');
      if (submitMessageSection) submitMessageSection.appendChild(errorMessage);
    }
  }

  /**
   * Updates location buttons for a specific product variant
   * @param {Object} variant - The variant object containing variant details
   */
  const updateLocationButtons = (variant) => {
    if (!variant) return;
    
    const pickupAvailability = document.querySelector(`pickup-availability[data-variant-id="${variant.id}"]`) || 
                              document.querySelector('pickup-availability');
    
    if (pickupAvailability) {
      // Remove existing event listeners from old buttons
      document.querySelectorAll('[data-location-id][data-location-name]').forEach(button => {
        button.removeEventListener('click', handleLocationButtonClick);
      });
      
      // Add event listeners to new buttons
      pickupAvailability.querySelectorAll('[data-location-id][data-location-name]').forEach(button => {
        button.addEventListener('click', handleLocationButtonClick);
      });
    }
  };

  /**
   * Handles location button click events
   * @param {Event} event - The click event object
   */
  const handleLocationButtonClick = (event) => {
    const button = event.currentTarget;
    const locationId = button.getAttribute('data-location-id');
    const locationName = button.getAttribute('data-location-name');
    
    // Reset form state before showing the form
    resetFormState();
    
    // Update form elements
    const selectedLocationName = document.getElementById('selected-location-name');
    const locationIdInput = document.querySelector('input[name="location_id"]');
    const formContainer = document.getElementById('click-and-collect-form__container');
    
    if (selectedLocationName) selectedLocationName.textContent = locationName;
    if (locationIdInput) locationIdInput.value = locationId;
    if (formContainer) formContainer.style.display = 'block';
    
    // Hide the locations section
    const locationsSection = button.closest('[id^="locations-section-"]');
    if (locationsSection) locationsSection.style.display = 'none';
  };

  // Set up initial event listeners
  document.querySelectorAll('[data-location-id][data-location-name]').forEach(button => {
    button.addEventListener('click', handleLocationButtonClick);
  });

  setupFormEventListeners();

  // Add event listener to drawer open events
  const drawers = document.querySelectorAll('x-drawer[id^="store-availability-drawer-"]');
  drawers.forEach(drawer => {
    // Listen for when drawer becomes visible
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          if (drawer.hasAttribute('open')) resetFormState();
        }
      });
    });
    
    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['open']
    });
  });

  // Handle variant changes
  document.addEventListener('variant:change', (event) => {
    resetFormState();
    updateLocationButtons(event.detail.variant);
    setupFormEventListeners();
  });
});